/**
 * AutoSaveIndicator Component
 *
 * Shows the current auto-save status (pending, saving, saved, error).
 * Used to provide feedback for auto-saved fields.
 */

'use client';

import { type AutoSaveStatus } from '../../hooks/useAutoSave';

export interface AutoSaveIndicatorProps {
  status: AutoSaveStatus;
  className?: string;
}

export function AutoSaveIndicator({ status, className = '' }: AutoSaveIndicatorProps) {
  if (status === 'idle') return null;

  const statusConfig = {
    pending: {
      text: 'Unsaved changes',
      icon: (
        <div className="w-2 h-2 rounded-full bg-feedback-warning animate-pulse" />
      ),
      textColor: 'text-feedback-warning',
    },
    saving: {
      text: 'Saving...',
      icon: (
        <svg
          className="w-4 h-4 animate-spin text-text-secondary"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ),
      textColor: 'text-text-secondary',
    },
    saved: {
      text: 'Saved',
      icon: (
        <svg
          className="w-4 h-4 text-feedback-success"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      ),
      textColor: 'text-feedback-success',
    },
    error: {
      text: 'Save failed',
      icon: (
        <svg
          className="w-4 h-4 text-feedback-error"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      ),
      textColor: 'text-feedback-error',
    },
  };

  const config = statusConfig[status];

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {config.icon}
      <span className={`text-xs ${config.textColor}`}>{config.text}</span>
    </div>
  );
}
